import productionLinesApi from '@/services/productionLines.service'
import type {
    ProductionLineCreateModel,
    ProductionLineModel
} from '@/types/models/ProductionLineModel'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import {
    EProductionLineActions,
    EProductionLineMutations,
    type ProductionLineState
} from './ProductionLineStoreTypes'
import { EAlertActions, EStoreGlobalModules } from '@rfc/rfc-components-library/store'
import i18n from '../../locales/i18n'

const state: Ref<PaginationDataType<ProductionLineState>> = ref({
    data: {
        productionLines: []
    },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getproductionLines: () => state.value.data?.productionLine,
    loading: () => state.value.loading
}

const actions = {
    async [EProductionLineActions.FETCH_PRODUCTION_LINES](
        { commit }: { commit: Commit },
        params?: Partial<PaginationType>
    ) {
        commit(EProductionLineMutations.SET_LOADING, true)
        return productionLinesApi
            .getProductionLines(params)
            .then(data => {
                commit(EProductionLineMutations.SET_PRODUCTION_LINE, data.data)
                commit(EProductionLineMutations.SET_PAGINATION, data)
                return data
            })
            .finally(() => {
                commit(EProductionLineMutations.SET_LOADING, false)
            })
    },

    async [EProductionLineActions.FETCH_PRODUCTION_LINE](
        { commit }: { commit: Commit },
        { id }: { id: number }
    ) {
        commit(EProductionLineMutations.SET_LOADING, true)
        return await productionLinesApi
            .getProductionLine(id)
            .then(res => {
                commit(EProductionLineMutations.SET_PRODUCTION_LINE, res.data)
                return res.data
            })
            .finally(() => commit(EProductionLineMutations.SET_LOADING, false))
    },

    async [EProductionLineActions.CREATE_PRODUCTION_LINE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        data: ProductionLineCreateModel
    ) {
        commit(EProductionLineMutations.SET_LOADING, true)
        return productionLinesApi
            .createProductionLine(data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EProductionLineActions.CREATE_PRODUCTION_LINE
                })
                return res.data
            })
            .finally(() => commit(EProductionLineMutations.SET_LOADING, false))
    },

    async [EProductionLineActions.UPDATE_PRODUCTION_LINE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, data }: { id: number; data: ProductionLineModel }
    ) {
        commit(EProductionLineMutations.SET_LOADING, true)
        return productionLinesApi
            .updateProductionLine(id, data)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EProductionLineActions.UPDATE_PRODUCTION_LINE
                })
            )
            .finally(() => {
                commit(EProductionLineMutations.SET_LOADING, false)
            })
    },

    async [EProductionLineActions.DELETE_PRODUCTION_LINE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, name }: { id: number; name: string }
    ) {
        await productionLinesApi.deleteProductionLine(id)
        commit(EProductionLineMutations.REMOVE_PRODUCTION_LINE, id)
        return productionLinesApi
            .deleteProductionLine(id)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: EProductionLineActions.DELETE_PRODUCTION_LINE
                })
            )
            .finally(() => commit(EProductionLineMutations.SET_LOADING, false))
    },

    [EProductionLineActions.RESET_PRODUCTION_LINE]({ commit }: { commit: Commit }) {
        commit(EProductionLineMutations.RESET_PRODUCTION_LINE)
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreGlobalModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`productionLines.api.success.${key}`, { name })
            },
            { root: true }
        )
    }
}

const mutations = {
    [EProductionLineMutations.SET_PRODUCTION_LINES](
        _: ProductionLineState,
        productionLines: ProductionLineModel[]
    ) {
        state.value.data!.productionLines = productionLines
    },
    [EProductionLineMutations.SET_PRODUCTION_LINE](
        _: ProductionLineState,
        productionLine: ProductionLineModel
    ) {
        state.value.data!.productionLine = productionLine
    },
    [EProductionLineMutations.SET_LOADING](_: ProductionLineState, loading: boolean) {
        state.value.loading = loading
    },
    [EProductionLineMutations.SET_PAGINATION](
        _: ProductionLineState,
        data: PaginationDataType<ProductionLineModel>
    ) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [EProductionLineMutations.REMOVE_PRODUCTION_LINE](_: ProductionLineState, id: number) {
        state.value.data!.productionLines =
            state.value.data?.productionLines?.filter(item => item.id !== id) ?? []
    },
    [EProductionLineMutations.RESET_PRODUCTION_LINE]() {
        state.value.data!.productionLine = undefined
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
